import React, { forwardRef, useMemo, useRef } from 'react';
import classNames from 'clsx';
import MenuContent from '../MenuContent/MenuContent';
import type { AnimatedSvgRef } from '../../../AnimatedIcon/types';
import type {
  BuilderAnimationIconComponent,
  BuilderAnimationIconProps,
} from '../../Menu.types';
import type { NavbarProps } from '../../Menu.types';
import directionStyles from '../../../../../common-styles/direction.module.scss';
import classes from './Navbar.module.scss';
import { selectors } from '../../constants';
import { useService } from '@wix/services-manager-react';
import type { IEnvironmentService } from '@wix/viewer-service-environment/definition';
import { EnvironmentDefinition } from '@wix/viewer-service-environment/definition';
import AnimatedIcon from '../../../AnimatedIcon/component';

const DEFAULT_ANIMATED_ICON_DURATION = 0.2;

export const NavbarMenu: React.FC<NavbarProps> = props => {
  const { getLanguageDirection } = useService(
    EnvironmentDefinition,
  ) as IEnvironmentService;
  const siteDirection = getLanguageDirection();

  const {
    id,
    className,
    submenuDirection,
    elementProps,
    direction = siteDirection,
    onItemClick,
    onItemDblClick,
    onItemMouseIn,
    onItemMouseOut,
  } = props;
  const { item } = elementProps ?? {};
  const animatedIconProps = item?.elementProps?.animatedIcon;
  const animatedIconPropsRef = useRef(animatedIconProps);

  animatedIconPropsRef.current = animatedIconProps;

  const BuilderAnimationIcon = useMemo<BuilderAnimationIconComponent>(() => {
    const AnimatedIconWithProps = forwardRef<
      AnimatedSvgRef,
      BuilderAnimationIconProps
    >(({ className: iconClassName, ...iconProps }, ref) => (
      <AnimatedIcon
        {...iconProps}
        duration={
          animatedIconPropsRef.current?.duration ??
          DEFAULT_ANIMATED_ICON_DURATION
        }
        svg={animatedIconPropsRef.current?.svg}
        className={classNames(
          selectors.animatedIcon,
          classes.itemIcon,
          animatedIconPropsRef.current?.className,
          iconClassName,
        )}
        ref={ref}
      />
    ));

    AnimatedIconWithProps.displayName = 'NavbarAnimatedIcon';

    return AnimatedIconWithProps;
  }, []);

  return (
    <MenuContent
      id={`${id}-menu-content`}
      navbarClassName={classNames(
        selectors.navbar,
        classes.navbar,
        className,
        classes[direction],
      )}
      directionClassName={directionStyles.fallbackDirection}
      direction={direction as any}
      submenuDirection={submenuDirection as any}
      onItemClick={onItemClick}
      BuilderAnimationIcon={
        animatedIconProps?.svg ? BuilderAnimationIcon : undefined
      }
      onItemDblClick={onItemDblClick}
      onItemMouseIn={onItemMouseIn}
      onItemMouseOut={onItemMouseOut}
      menuElementProps={elementProps}
    />
  );
};
